import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import { ReactNode, CSSProperties } from 'react';

interface Props {
  checked: boolean;
  onToggle: () => void;
  children: ReactNode;
  /** Visual variant — 'plain' (no chip border), 'chip' (bordered, fills colour when on) */
  variant?: 'plain' | 'chip';
  /** Colour family when checked — defaults to brand red */
  intent?: 'brand' | 'success' | 'warning' | 'danger';
  /** Accent rail colour (variant='chip' only). Defaults to brand red */
  accent?: string;
}

const useStyles = makeStyles({
  rowBase: {
    width: '100%',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minHeight: '52px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '15px',
    lineHeight: 1.35,
    color: tokens.colorNeutralForeground1,
    WebkitTapHighlightColor: 'transparent',
    ':active': { opacity: 0.7 },
  },
  plain: {
    backgroundColor: 'transparent',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '1px',
    borderLeftWidth: '0',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  chip: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: '8px',
  },
  chipOn: {
    backgroundColor: '#FCE8E9',
    borderTopColor: '#E3272D',
    borderRightColor: '#E3272D',
    borderBottomColor: '#E3272D',
    borderLeftColor: '#E3272D',
    color: '#6F0F12',
    fontWeight: 600,
  },
  cb: {
    flexShrink: 0,
    width: '24px', height: '24px', display: 'grid', placeItems: 'center',
    border: `1px solid ${tokens.colorNeutralStroke1}`, borderRadius: '4px',
    color: tokens.colorBrandForeground1, backgroundColor: tokens.colorNeutralBackground1,
  },
  label: {
    flex: 1,
    minWidth: 0,
  },
});

export function TapRow({
  checked,
  onToggle,
  children,
  variant = 'plain',
  accent = '#E3272D',
}: Props) {
  const styles = useStyles();
  const cls = mergeClasses(
    styles.rowBase,
    variant === 'plain' ? styles.plain : styles.chip,
    variant === 'chip' && checked && styles.chipOn,
  );

  // For 'chip' on-state we override the checkbox accent so it picks up the
  // chip's intent colour; for 'plain' we let it default to brand red.
  const overrideStyle: CSSProperties | undefined = variant === 'chip' && checked
    ? { borderColor: accent }
    : undefined;

  return (
    <button
      type="button"
      className={cls}
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      style={overrideStyle}
    >
      <span className={styles.cb} aria-hidden="true">{checked ? '✓' : ''}</span>
      <span className={styles.label}>{children}</span>
    </button>
  );
}
